import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';


const InteractiveDashboardPreview = () => {
  const [selectedChart, setSelectedChart] = useState('line');
  const [selectedMetric, setSelectedMetric] = useState('deals');
  const [animationPhase, setAnimationPhase] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);

  const metrics = [
    { key: 'deals', label: 'Deals', icon: '💰', value: 247, trend: 15.3, color: '#3B82F6' },
    { key: 'response', label: 'Response Rate', icon: '📊', value: 89, trend: 8.7, color: '#10B981', suffix: '%' },
    { key: 'inmails', label: 'InMails', icon: '📨', value: 1456, trend: 23.1, color: '#F59E0B' },
    { key: 'interviews', label: 'Interviews', icon: '👥', value: 142, trend: 12.8, color: '#8B5CF6' }
  ];

  const chartData = [
    { month: 'Jan', deals: 45, response: 78, inmails: 1200, interviews: 98 },
    { month: 'Feb', deals: 52, response: 82, inmails: 1350, interviews: 115 },
    { month: 'Mar', deals: 48, response: 85, inmails: 1280, interviews: 108 },
    { month: 'Apr', deals: 61, response: 89, inmails: 1456, interviews: 142 },
    { month: 'May', deals: 58, response: 91, inmails: 1398, interviews: 138 }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase(prev => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const renderChart = () => {
    const maxValue = Math.max(...chartData.map(d => d[selectedMetric]));
    const currentMetric = metrics.find(m => m.key === selectedMetric);

    if (selectedChart === 'line') {
      const points = chartData.map((d, i) => {
        const x = 50 + (i * 200);
        const y = 200 - (d[selectedMetric] / maxValue) * 150;
        return { x, y, value: d[selectedMetric] };
      });

      const pathD = points.reduce((path, point, i) => {
        return path + (i === 0 ? `M ${point.x} ${point.y}` : ` L ${point.x} ${point.y}`);
      }, '');

      return (
        <svg className="w-full h-64" viewBox="0 0 900 250">
          {/* Grid lines */}
          {[0, 1, 2, 3, 4].map(i => (
            <line
              key={i}
              x1="50"
              x2="850"
              y1={50 + i * 37.5}
              y2={50 + i * 37.5}
              stroke="#E5E7EB"
              strokeWidth="1"
            />
          ))}
          
          {/* Chart line */}
          <path
            d={pathD}
            fill="none"
            stroke={currentMetric.color}
            strokeWidth="3"
            className="animate-pulse"
          />
          
          {/* Data points */}
          {points.map((point, i) => (
            <g key={i}>
              <circle
                cx={point.x}
                cy={point.y}
                r="6"
                fill={currentMetric.color}
                className="hover:r-8 transition-all cursor-pointer"
                style={{
                  animation: `bounce 2s ease-in-out infinite ${i * 0.2}s`
                }}
              />
              <text
                x={point.x}
                y={point.y - 15}
                textAnchor="middle"
                className="text-xs fill-gray-600 font-medium"
              >
                {point.value}{currentMetric.suffix || ''}
              </text>
            </g>
          ))}
          
          {/* X-axis labels */}
          {chartData.map((d, i) => (
            <text
              key={i}
              x={50 + (i * 200)}
              y={230}
              textAnchor="middle"
              className="text-sm fill-gray-500"
            >
              {d.month}
            </text>
          ))}
        </svg>
      );
    } else if (selectedChart === 'bar') {
      return (
        <svg className="w-full h-64" viewBox="0 0 900 250">
          {/* Grid lines */}
          {[0, 1, 2, 3, 4].map(i => (
            <line
              key={i}
              x1="50"
              x2="850"
              y1={50 + i * 37.5}
              y2={50 + i * 37.5}
              stroke="#E5E7EB"
              strokeWidth="1"
            />
          ))}
          
          {/* Bars */}
          {chartData.map((d, i) => {
            const barHeight = (d[selectedMetric] / maxValue) * 150;
            const x = 80 + (i * 160);
            return (
              <rect
                key={i}
                x={x}
                y={200 - barHeight}
                width="80"
                height={barHeight}
                fill={currentMetric.color}
                className="hover:opacity-80 transition-opacity cursor-pointer"
                style={{
                  animation: `slideUp 1s ease-out ${i * 0.1}s both`
                }}
              />
            );
          })}
          
          {/* X-axis labels */}
          {chartData.map((d, i) => (
            <text
              key={i}
              x={120 + (i * 160)}
              y={230}
              textAnchor="middle"
              className="text-sm fill-gray-500"
            >
              {d.month}
            </text>
          ))}
        </svg>
      );
    } else {
      // Pie chart
      const total = chartData.reduce((sum, d) => sum + d[selectedMetric], 0);
      let currentAngle = -90;
      const radius = 80;
      const centerX = 450;
      const centerY = 125;

      return (
        <svg className="w-full h-64" viewBox="0 0 900 250">
          {chartData.map((d, i) => {
            const sliceAngle = (d[selectedMetric] / total) * 360;
            const startAngle = currentAngle;
            const endAngle = currentAngle + sliceAngle;
            
            const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
            const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
            const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
            const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);
            
            const largeArc = sliceAngle > 180 ? 1 : 0;
            
            const pathData = [
              `M ${centerX} ${centerY}`,
              `L ${x1} ${y1}`,
              `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
              'Z'
            ].join(' ');
            
            currentAngle += sliceAngle;
            
            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'];
            
            return (
              <path
                key={i}
                d={pathData}
                fill={colors[i]}
                className="hover:opacity-80 transition-opacity cursor-pointer"
                style={{
                  animation: `fadeIn 1s ease-out ${i * 0.2}s both`
                }}
              />
            );
          })}
          
          {/* Center circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r="30"
            fill="white"
            stroke="#E5E7EB"
            strokeWidth="2"
          />
          <text
            x={centerX}
            y={centerY}
            textAnchor="middle"
            dy="0.3em"
            className="text-lg font-bold fill-gray-700"
          >
            {total}
          </text>
        </svg>
      );
    }
  };

  return (
    <div className=" py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        

        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Analysez vos Performances
            <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              en suivant vos KPI
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Suivez de près vos performances et visualisez l'évolution de votre activité au fil du temps via un graphique interactif
          </p>
        </div>

        {/* Interactive Dashboard */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
          {/* Dashboard Header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-8 py-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
              </div>
              <div className="flex space-x-2">
                {['line', 'bar', 'pie'].map((chart) => (
                  <button
                    key={chart}
                    onClick={() => setSelectedChart(chart)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedChart === chart
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                    }`}
                  >
                    {chart.charAt(0).toUpperCase() + chart.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              {metrics.map((metric, index) => (
                <div
                  key={metric.key}
                  className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                    hoveredCard === index
                      ? 'shadow-xl scale-105 border-blue-500'
                      : selectedMetric === metric.key
                      ? 'shadow-lg border-blue-400 bg-blue-50'
                      : 'shadow-md border-gray-200 hover:shadow-lg hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedMetric(metric.key)}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-3xl">{metric.icon}</div>
                    <div className={`text-sm font-semibold px-2 py-1 rounded-full ${
                      metric.trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {metric.trend > 0 ? '↗' : '↘'} {Math.abs(metric.trend)}%
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <div className="text-3xl font-bold text-gray-900">
                      {metric.value.toLocaleString()}{metric.suffix || ''}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">{metric.label}</div>
                  </div>
                  
                  {/* Mini progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-1000"
                      style={{
                        width: `${(metric.value / Math.max(...metrics.map(m => m.value))) * 100}%`,
                        backgroundColor: metric.color,
                        animation: `fillBar 2s ease-out ${index * 0.2}s both`
                      }}
                    ></div>
                  </div>
                  
                  {/* Active indicator */}
                  {selectedMetric === metric.key && (
                    <div className="absolute top-2 right-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Chart Area */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h4 className="text-xl font-semibold text-gray-900">
                  {metrics.find(m => m.key === selectedMetric)?.label} Trends
                </h4>
                <div className="flex space-x-2">
                  {metrics.map((metric) => (
                    <button
                      key={metric.key}
                      onClick={() => setSelectedMetric(metric.key)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        selectedMetric === metric.key
                          ? 'text-white shadow-lg'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                      style={{
                        backgroundColor: selectedMetric === metric.key ? metric.color : undefined
                      }}
                    >
                      {metric.icon} {metric.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Chart Container */}
              <div className="bg-white rounded-xl p-4 shadow-sm">
                {renderChart()}
              </div>
            </div>

            
          </div>
          
        </div>
      </div>

      {/* Custom animations */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes fillBar {
          from {
            width: 0%;
          }
          to {
            width: var(--final-width);
          }
        }
        
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            transform: translate3d(0,-8px,0);
          }
          70% {
            transform: translate3d(0,-4px,0);
          }
          90% {
            transform: translate3d(0,-2px,0);
          }
        }
      `}</style>
    </div>
  );
};

export default InteractiveDashboardPreview;